import CTNMessage from '@ctn/ui/components/ctn-message';
import CTNModal from '@ctn/ui/components/ctn-modal';
import { useNotification } from '@ctn/ui/components/ctn-notifications';
import { type CTNModalProps } from '@ctn/ui/types/ctn-modal';
import classNames from 'classnames';
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { FormProvider, useForm, type SubmitErrorHandler } from 'react-hook-form';

import { PersonaFisica, PersonaJuridica, TipoInterviniente } from '@/components/Interviniente';
import { getDefaultValues, resolver, schema, type SujetoForm } from '@/components/Interviniente/config';
import { useGlobalContext } from '@/context/global.context';
import { useSolicitudContext } from '@/context/solicitud.context';

import ERROR_MESSAGE, { ERROR_TYPE } from '@/constants/errors.constants';
import { ID_PAIS, TIPO_PERSONA } from '@/constants/tramite.constants';
import { mapSujetoDBToForm, mapSujetoFormToDB } from '@/helpers/formSujeto';
import { notificationsConfig } from '@/helpers/notifications';
import { peekErrors, peekValidation } from '@/helpers/validations';
import { type ISujetoListado } from '@/interfaces/model/don.interface';
import { type TipoIntervencion } from '@/interfaces/model/maestros.interface';

type ModalParams = {
	readonly type: string;
	readonly isOpen: boolean;
	readonly onClose: () => void;
	readonly onSave: (param: ISujetoListado) => void;
	readonly data?: ISujetoListado;
	readonly isDisplayTipoIntervencion?: boolean;
	readonly filterIntervencion?: number[];
};

const ModalInterviniente = ({
	type,
	isOpen,
	onClose,
	data,
	filterIntervencion,
	isDisplayTipoIntervencion,
	onSave,
}: ModalParams): ReactElement => {
	const {
		maestrosTramite: { tipoDocumentoIdent, tipoPersona, tipoIntervencion },
		maestrosGeneric: { tipoDireccion },
	} = useGlobalContext();
	const [showData, setShowData] = useState(false);
	const [isHidden, setIsHidden] = useState(false);
	const [isDisabledContinue, setIsDisabledContinue] = useState(false);
	const [isDisabledAdd, setIsDisabledAdd] = useState(false);
	const [disableActions, setDisableActions] = useState(false);
	const { openNotification, closeNotification } = useNotification();
	const { getFormValues } = useSolicitudContext();
	const notificationTemplate = notificationsConfig({ closeNotification });

	const methods = useForm<SujetoForm>({
		mode: 'onSubmit',
		resolver: resolver({ globalValues: getFormValues() }),
		defaultValues: getDefaultValues(),
	});

	const { reset, trigger, handleSubmit, watch, setValue } = methods;

	const idTipoPersona = watch('idTipoPersona');
	const telefonoCodigoPais = watch('telefonoCodigoPais');
	const isPersonaFisica = idTipoPersona === String(TIPO_PERSONA.FISICA);
	const isPersonaJuridica = idTipoPersona === String(TIPO_PERSONA.JURIDICA);

	const onSubmit = async (data: SujetoForm) => {
		const dataMapped = mapSujetoFormToDB(data, {
			tipoDocumentoIdent,
			tipoIntervencion,
			tipoPersona,
			tipoDireccion,
		});

		onSave(dataMapped);
		onClose();
	};

	const onContinue = async () => {
		const isValidTipoPersona = await trigger('idTipoPersona');
		const isValidTipoIntervencion = await trigger('idTipoIntervencion');
		if (isValidTipoPersona && isValidTipoIntervencion) {
			setShowData(true);
		}
	};

	const onInvalid: SubmitErrorHandler<SujetoForm> = (errors) => {
		if (errors?.representantes) {
			setIsHidden(true);
			const onClose = () => setIsHidden(false);
			openNotification(notificationTemplate.erroresForm(errors.representantes.message as string, onClose));
		}

		if (errors?.numeroDocumento?.type === ERROR_TYPE.duplicatedFisica) {
			setIsHidden(true);
			const onClose = () => setIsHidden(false);
			openNotification(notificationTemplate.erroresForm(ERROR_MESSAGE.duplicatedFisica, onClose));
		}

		if (errors?.numeroDocumento?.type === ERROR_TYPE.duplicatedJuridico) {
			setIsHidden(true);
			const onClose = () => setIsHidden(false);
			openNotification(notificationTemplate.erroresForm(ERROR_MESSAGE.duplicatedJuridico, onClose));
		}

		if (errors?.email) {
			setIsHidden(true);
			const onClose = () => setIsHidden(false);
			openNotification(notificationTemplate.erroresForm(ERROR_MESSAGE.emailNoValido, onClose));
		}
	};

	const actionData: CTNModalProps['actionButtons'] = showData
		? [
			{
				key: 'primary',
				mode: 'primary',
				label: data ? 'Actualizar' : 'Añadir',
				minWidth: '140',
				size: 'size-40',
				disabled: disableActions || isDisabledAdd,
				onClick(e) {
					handleSubmit(onSubmit, onInvalid)(e);
				},
				
			},
		]
		: [
			{
				key: 'primary',
				mode: 'primary',
				label: 'Continuar',
				size: 'size-40',
				minWidth: '140',
				disabled: disableActions || isDisabledContinue,
				onClick() {
					onContinue();
				},
			},
		];

	const actionButtons: CTNModalProps['actionButtons'] = [
		{
			key: 'tertiary',
			mode: 'tertiary',
			label: 'Cancelar',
			size: 'size-40',
			minWidth: '140',
			disabled: disableActions,
			onClick: onClose,
		},
		...actionData,
	];

	const availablesTipoIntervencion = useMemo(() => {
		if (!filterIntervencion) return tipoIntervencion;

		return tipoIntervencion.filter(({ id }) => filterIntervencion.includes(id));
	}, [tipoIntervencion, filterIntervencion]);

	/**
	 * Poner prestablecido el idTipoIntervencion si
	 * el filtro de tipoIntervención obtiene un resultado y se indica como displayOnly.
	 */
	const getDefaultIdTipoIntervencion = (tipoIntervencionMaestro: TipoIntervencion[], filter?: number[]) => {
		if (!filter || !isDisplayTipoIntervencion) return '';

		const tipoIntervencion = tipoIntervencionMaestro.filter(({ id }) => filter.includes(id));
		if (tipoIntervencion.length === 1) {
			return String(tipoIntervencion[0].id);
		}
	};

	const resetModal = () => {
		setShowData(false);
		setIsHidden(false);
		setIsDisabledContinue(false);
		setDisableActions(false);
	};

	useEffect(() => {
		if (!isOpen) {
			resetModal();
			return;
		}
		const idTipoIntervencion = getDefaultIdTipoIntervencion(tipoIntervencion, filterIntervencion);
		let dataMapped: SujetoForm = getDefaultValues({ idTipoIntervencion });

		if (data) {
			dataMapped = mapSujetoDBToForm(data);
		}

		reset(dataMapped);

		if (data) {
			onContinue();
		} else {
			setShowData(false);
		}
	}, [isOpen, data]);

	useEffect(() => {
		if (isPersonaFisica && !telefonoCodigoPais) {
			setValue('telefonoCodigoPais', ID_PAIS.ESPANA);
		}
	}, [isPersonaFisica]);

	useEffect(() => {
		const check = async () => {
			const isValidFirstSection = await peekValidation({
				schema: schema({ globalValues: getFormValues() }),
				data: watch(),
				fields: ['idTipoIntervencion', 'idTipoPersona'],
			});
			setIsDisabledContinue(!isValidFirstSection);
			
			const errors = await peekErrors({
				schema: schema({ globalValues: getFormValues() }),
				data: watch()
			});
			const hasErrorsRequired = errors.some(({ type }) => type === ERROR_TYPE.required);
			setIsDisabledAdd(hasErrorsRequired);

			
		};

		check();
	}, [watch(), schema]);

	return (
		<CTNModal
			autoHeight
			closeable
			className={classNames('c-pnp-modal', { 'u-hidden': isHidden })}
			type="basic"
			title={data ? `Editar ${type}` : `Añadir ${type}`}
			size="xxl"
			alignButtons="right"
			actionButtons={actionButtons}
			show={isOpen}
			content={
				<div className="u-flow">
					{availablesTipoIntervencion.length === 0 && (
						<p>
							Este acto jurídico no requiere la participación de {type}, por lo que no es necesario
							completar más datos en este apartado.
						</p>
					)}
					<FormProvider {...methods}>
						<form className="[ o-grid ] [ c-pnp-form ]">
							<div className="u-flow">
								{!showData && availablesTipoIntervencion.length > 0 && (
									<CTNMessage
										content="Selecciona el tipo de persona (físicia o jurídica) y pulsa el botón “Continuar” para introducir los datos del tipo de persona seleccionado."
										icon="info"
										status="info"
										mode="outline"
										closable={false}
									/>
								)}
								<TipoInterviniente
									isDisabled={showData}
									isDisplayTipoIntervencion={isDisplayTipoIntervencion}
									filterIntervencion={filterIntervencion}
								/>
							</div>
							{isPersonaFisica && showData && <PersonaFisica />}
							{isPersonaJuridica && showData && <PersonaJuridica setDisableActions={setDisableActions} />}
						</form>
					</FormProvider>
				</div>
			}
			onClose={onClose}
		/>
	);
};

export default ModalInterviniente;
